import { type VariantProps, cva } from "class-variance-authority";
import Link, { type LinkProps } from "next/link";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";

import { ButtonContent } from "@/components/ui/buttons/shared/ButtonContent";
import { uiOutlineClass } from "@/components/ui/global/outline";
import { TooltipEllipsis } from "@/components/ui/overlays/Tooltip/TooltipEllipsis";

export const button = cva(
  [
    "flex items-center justify-center gap-icon-to-label-spacing rounded-button-rounding",
    "cursor-pointer focus-visible:outline-neutral-100",
    uiOutlineClass,
  ],
  {
    variants: {
      width: {
        s: "w-full max-w-4-5xs",
        m: "w-full max-w-3-5xs",
        l: "w-full max-w-3xs",
        fit: "w-full",
        auto: "w-auto",
      },
      size: {
        small: "p-height-s",
        default: "p-height-m",
        large: "p-height-l",
      },
      iconPosition: {
        left: "flex-row",
        right: "flex-row-reverse",
      },
      variant: {
        primary: [
          "bg-white",
          "text-black",
          "hover:bg-neutral-75",
          "hover:text-secondary-400",
          "active:bg-secondary-100",
          "disabled:bg-neutral-75",
        ],
        secondary: [
          "bg-secondary-200",
          "text-secondary-50",
          "hover:bg-secondary-500",
          "active:bg-secondary-100",
          "disabled:bg-neutral-200 disabled:text-neutral-100",
        ],
        outlined: [
          "border",
          "border-white",
          "text-white",
          "hover:border-secondary-75",
          "hover:text-secondary-75",
          "active:border-secondary-500",
          "active:text-secondary-500",
          "focus-visible:outline-neutral-100",
          "disabled:border-secondary-100",
          "disabled:text-secondary-100",

          // styles for ToggleButton - we include them here to avoid code duplication
          "selected:bg-secondary-400",
          "selected:text-white",
          "selected:hover:bg-secondary-100",
          "selected:hover:text-white",
          "selected:disabled:bg-secondary-75",
        ],
        signup: [
          "border",
          "border-secondary-200",
          "text-secondary-400",
          "hover:border-secondary-100",
          "hover:text-secondary-100",
          "active:border-secondary-500",
          "active:text-secondary-500",
          "focus-visible:outline-neutral-100",

          "disabled:border-secondary-75",
          "disabled:text-secondary-75",
        ],
        underlined: [
          "w-auto rounded-none border-none",

          "text-white",
          "hover:text-neutral-100",
          "disabled:text-secondary-100",
          "underline",
        ],
        text: ["border-none", "text-white", "hover:text-neutral-100", "disabled:text-secondary-100"],
        tertiary: ["bg-neutral-75", "hover:bg-secondary-75", "!p-2"],
        navbar: ["bg-neutral-500", "hover:bg-neutral-400", "rounded-full"],
        black: [
          "bg-secondary-500",
          "text-white",
          "hover:bg-secondary-200",
          "active:bg-secondary-100",
          "disabled:bg-neutral-400 disabled:text-neutral-100",
        ],
      },
    },
    defaultVariants: {
      width: "fit",
      size: "default",
      variant: "primary",
      iconPosition: "left",
    },
  },
);

type ButtonVariantProps = VariantProps<typeof button>;

type ButtonProps = ButtonVariantProps &
  AriaButtonProps & {
    children?: string | React.ReactNode;
    isLoading?: boolean;
    icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    link?: LinkProps & { target?: string };
    hideText?: boolean;
    noDisableWhenLoading?: boolean;
    iconClassName?: string;
    labelClassName?: string;
  };

export const Button = ({
  width,
  size,
  variant,
  icon: Icon,
  iconPosition,
  children,
  isLoading,
  className,
  link,
  hideText,
  noDisableWhenLoading,
  iconClassName = "h-6 w-6",
  labelClassName,
  ...props
}: ButtonProps) => {
  const Component: any = link ? Link : AriaButton;

  return (
    <TooltipEllipsis text={typeof children === "string" ? children : ""}>
      {(onContentRef) => (
        <Component
          {...props}
          {...link}
          isDisabled={props.isDisabled || (isLoading && !noDisableWhenLoading)}
          className={button({ width, size, variant, iconPosition, className })}
        >
          <ButtonContent
            iconClassName={iconClassName}
            className={labelClassName}
            isLoading={isLoading}
            icon={Icon}
            text={typeof children === "string" ? children : ""}
            ref={onContentRef}
            hideText={hideText}
          />
          {typeof children !== "string" && children}
        </Component>
      )}
    </TooltipEllipsis>
  );
};
